'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onBookingClick: () => void;
}

export default function HeroSection({ onBookingClick }: HeroSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroSlides = [
  {
    image: "/assets/images/Entrance-1767660169249.jpg",
    alt: 'Entrance of Proximus serviced apartments with clear signage and modern facade',
    title: 'Where Business Meets Comfort',
    subtitle: 'Premium serviced apartments in the heart of Bangalore\'s corporate corridor'
  },
  {
    image: "/assets/images/tmpr_0b3aia-1767661959795.jpg",
    alt: 'Complete Proximus building facade exterior with Single Teas cafe at street level',
    title: 'Your Premium Destination',
    subtitle: 'Modern serviced apartments in a prime Bangalore location with convenient amenities'
  },
  {
    image: "/assets/images/Reception1-1767660447151.jpg",
    alt: 'Elegant marble reception desk with PX logo at Proximus serviced apartments lobby',
    title: 'Welcome to Excellence',
    subtitle: 'Step into our sophisticated lobby designed for your comfort and convenience'
  },
  {
    image: "/assets/images/tmp_0a01in5-1767660801247.jpg",
    alt: 'Modern bedroom interior with comfortable furnishings in Proximus serviced apartment',
    title: 'Your Home Away From Home',
    subtitle: 'Relax in our beautifully appointed apartments with premium amenities'
  },
  {
    image: "/assets/images/IMG20251021161728-1767660889621.jpg",
    alt: 'Modern living room interior with contemporary furniture and bright natural lighting in Proximus serviced apartment',
    title: 'Spacious Living Areas',
    subtitle: 'Enjoy our thoughtfully designed living spaces perfect for work and relaxation'
  },
  {
    image: "/assets/images/tmp26nw87ej-1767661015709.jpg",
    alt: 'Elegant living room interior with brown sofa and TV unit in Proximus serviced apartment',
    title: 'Comfortable Living Spaces',
    subtitle: 'Discover our well-appointed apartments featuring modern amenities and stylish interiors'
  },
  {
    image: "/assets/images/tmpbpowa8mh-1767661098856.jpg",
    alt: 'Modern apartment bedroom interior with contemporary design and comfortable furnishings in Proximus serviced apartment',
    title: 'Restful Bedroom Retreats',
    subtitle: 'Unwind in our elegantly designed bedrooms crafted for your ultimate comfort'
  }];

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, heroSlides.length]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  if (!isHydrated) {
    return (
      <section className="relative h-[600px] lg:h-[700px] bg-gray-100">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center space-y-4 px-4">
            <h1 className="text-4xl lg:text-6xl font-display font-bold text-primary">
              Where Business Meets Comfort
            </h1>
            <p className="text-lg lg:text-xl text-text-secondary max-w-2xl mx-auto">
              Premium serviced apartments in the heart of Bangalore&apos;s corporate corridor
            </p>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="relative h-[600px] lg:h-[700px] overflow-hidden">
      {heroSlides.map((slide, index) =>
      <div
        key={index}
        className={`absolute inset-0 transition-opacity duration-1000 ${
        index === currentSlide ? 'opacity-100' : 'opacity-0'}`
        }>

          <div className="relative h-full w-full">
            <AppImage
            src={slide.image}
            alt={slide.alt}
            className="w-full h-full object-cover"
            priority={index === 0} />

            <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-transparent" />
          </div>

          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-4 lg:px-8">
              <div className="max-w-2xl space-y-6">
                <h1 className="text-4xl lg:text-6xl font-display font-bold text-white leading-tight">
                  {slide.title}
                </h1>
                <p className="text-lg lg:text-xl text-white/90">
                  {slide.subtitle}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <a
                    href="https://bookingengine.stayflexi.com/?hotel_id=33526"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-8 py-4 bg-accent hover:bg-accent/90 text-white font-semibold rounded-lg shadow-elevated transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2">
                    <span>Check Availability</span>
                    <Icon name="CalendarIcon" size={20} className="text-white" />
                  </a>
                  <a
                    href="/gallery"
                    className="px-8 py-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold rounded-lg shadow-elevated transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2 border border-white/40">
                    <span>Gallery</span>
                    <Icon name="PhotoIcon" size={20} className="text-white" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handlePrevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-300 z-10"
        aria-label="Previous slide">

        <Icon name="ChevronLeftIcon" size={24} className="text-white" />
      </button>

      <button
        onClick={handleNextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-300 z-10"
        aria-label="Next slide">

        <Icon name="ChevronRightIcon" size={24} className="text-white" />
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
        {heroSlides.map((_, index) =>
        <button
          key={index}
          onClick={() => setCurrentSlide(index)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${
          index === currentSlide ? 'bg-white w-8' : 'bg-white/50'}`
          }
          aria-label={`Go to slide ${index + 1}`} />

        )}
      </div>
    </section>);

}